import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { format, addHours, isBefore, isSameDay } from "date-fns";
import { es, enUS } from "date-fns/locale";
import UsdHint from "./UsdHint";
import {
  CalendarIcon,
  Clock,
  MapPin,
  AlertCircle,
  Loader2,
} from "lucide-react";

// Generate 30-min interval slots from 06:00 to 23:30
function generateTimeSlots() {
  const slots = [];
  for (let h = 6; h <= 23; h++) {
    for (const m of [0, 30]) {
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      const display = format(new Date(2000, 0, 1, h, m), "h:mm a");
      slots.push({ value, display });
    }
  }
  return slots;
}

const TIME_SLOTS = generateTimeSlots();

function combineDateAndTime(date, hhmm) {
  if (!date || !hhmm) return null;
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  const out = new Date(date);
  out.setHours(h, m, 0, 0);
  return out;
}

function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

/**
 * TripScheduler — Turo-style "Your Trip" date+time selector with validation.
 *
 * Props:
 *  - vehicleId (string) — used to fetch availability for conflict detection
 *  - dailyRate (number)
 *  - weeklyDiscountPct (number) - e.g. 10 = 10% off >= 7 days
 *  - monthlyDiscountPct (number)
 *  - locationLabel (string)
 *  - minHours (default 1)
 *  - initialPickup (Date) / initialReturn (Date)
 *  - onChange({pickup, returnDate, valid, conflict, durationDays})
 *  - onContinue() — called when user clicks Continuar (only if valid)
 *  - submitting (bool) — disables CTA
 */
export default function TripScheduler({
  vehicleId,
  dailyRate = 0,
  weeklyDiscountPct = 0,
  monthlyDiscountPct = 0,
  locationLabel = "",
  minHours = 1,
  initialPickup,
  initialReturn,
  onChange,
  onContinue,
  submitting = false,
}) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith("en") ? enUS : es;
  // Pre-fill: tomorrow 10:00 AM → day after 10:00 AM
  const defaultPickup = initialPickup || (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    return d;
  })();
  const defaultReturn = initialReturn || addHours(defaultPickup, 24);

  const [pickupDate, setPickupDate] = useState(defaultPickup);
  const [pickupTime, setPickupTime] = useState(format(defaultPickup, "HH:mm"));
  const [returnDate, setReturnDate] = useState(defaultReturn);
  const [returnTime, setReturnTime] = useState(format(defaultReturn, "HH:mm"));
  const [pickupCalOpen, setPickupCalOpen] = useState(false);
  const [returnCalOpen, setReturnCalOpen] = useState(false);

  const [unavailable, setUnavailable] = useState([]); // [{start, end}]
  const [loadingAvail, setLoadingAvail] = useState(false);

  // Fetch availability conflicts
  useEffect(() => {
    if (!vehicleId) return;
    let cancelled = false;
    (async () => {
      setLoadingAvail(true);
      try {
        const { data } = await api.get(`/vehicles/${vehicleId}/availability`);
        const blocked = [];
        for (const b of data.booked_dates || []) {
          if (b.pickup_date && b.return_date) {
            blocked.push({
              start: new Date(b.pickup_date),
              end: new Date(b.return_date),
              reason: "Reservado",
            });
          }
        }
        for (const blk of data.blocks || []) {
          if (blk.start_date && blk.end_date) {
            blocked.push({
              start: new Date(blk.start_date),
              end: new Date(blk.end_date),
              reason: blk.reason || "Bloqueado",
            });
          }
        }
        if (!cancelled) setUnavailable(blocked);
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoadingAvail(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vehicleId]);

  const pickup = useMemo(() => combineDateAndTime(pickupDate, pickupTime), [pickupDate, pickupTime]);
  const ret = useMemo(() => combineDateAndTime(returnDate, returnTime), [returnDate, returnTime]);

  const validation = useMemo(() => {
    if (!pickup || !ret) return { valid: false, error: t("tripScheduler.selectDateTime") };
    if (isBefore(pickup, new Date())) {
      return { valid: false, error: t("tripScheduler.startInFuture") };
    }
    const diffMs = ret - pickup;
    if (diffMs <= 0) {
      return { valid: false, error: t("tripScheduler.endAfterStart") };
    }
    const diffHours = diffMs / (1000 * 60 * 60);
    if (diffHours < minHours) {
      return { valid: false, error: t("tripScheduler.minDuration", { count: minHours }) };
    }
    return { valid: true, error: null };
  }, [pickup, ret, minHours, t]);

  const conflict = useMemo(() => {
    if (!pickup || !ret) return null;
    for (const u of unavailable) {
      if (rangesOverlap(pickup, ret, u.start, u.end)) {
        return u;
      }
    }
    return null;
  }, [pickup, ret, unavailable]);

  const isDateUnavailable = (date) => {
    return unavailable.some((u) => {
      // mark whole day unavailable if it overlaps any blocked range
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);
      return rangesOverlap(dayStart, dayEnd, u.start, u.end);
    });
  };

  // Suggest next available
  const suggestNext = () => {
    if (!conflict) return;
    const newPickup = new Date(conflict.end);
    newPickup.setMinutes(newPickup.getMinutes() + 1);
    const newReturn = addHours(newPickup, 24);
    setPickupDate(newPickup);
    setPickupTime(format(newPickup, "HH:mm"));
    setReturnDate(newReturn);
    setReturnTime(format(newReturn, "HH:mm"));
  };

  // Bubble changes up
  useEffect(() => {
    onChange?.({
      pickup,
      returnDate: ret,
      valid: validation.valid && !conflict,
      conflict,
      durationHours: pickup && ret ? Math.round((ret - pickup) / 3600000) : 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup?.getTime?.(), ret?.getTime?.(), validation.valid, conflict?.start?.getTime?.()]);

  // Price summary (client-side estimate; canonical price is from /bookings/calculate)
  const priceSummary = useMemo(() => {
    if (!pickup || !ret || !validation.valid) return null;
    const ms = ret - pickup;
    const days = Math.max(1, Math.ceil(ms / (1000 * 60 * 60 * 24)));
    const subtotal = dailyRate * days;
    let discountPct = 0;
    if (days >= 30 && monthlyDiscountPct > 0) discountPct = monthlyDiscountPct;
    else if (days >= 7 && weeklyDiscountPct > 0) discountPct = weeklyDiscountPct;
    const discounted = Math.round(subtotal * (1 - discountPct / 100));
    return { days, subtotal, discounted, discountPct };
  }, [pickup, ret, validation.valid, dailyRate, weeklyDiscountPct, monthlyDiscountPct]);

  const ctaDisabled = !validation.valid || !!conflict || submitting;

  return (
    <div className="space-y-5" data-testid="trip-scheduler">
      <h2 className="font-heading font-semibold text-lg">{t("tripScheduler.yourTrip")}</h2>

      {/* Trip Start */}
      <div>
        <Label className="text-xs uppercase tracking-wide text-neutral-500">{t("tripScheduler.tripStart")}</Label>
        <div className="grid grid-cols-2 gap-2 mt-1.5">
          <Popover open={pickupCalOpen} onOpenChange={setPickupCalOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal h-12 bg-neutral-50 border-neutral-200 hover:bg-neutral-100 rounded-xl"
                data-testid="trip-pickup-date-btn"
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-neutral-500" />
                <span className="truncate">
                  {pickupDate ? format(pickupDate, "MM/dd/yyyy") : t("tripScheduler.date")}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={pickupDate}
                onSelect={(d) => {
                  if (!d) return;
                  setPickupDate(d);
                  // If return is now before pickup, push it forward
                  if (returnDate && isBefore(returnDate, d)) {
                    setReturnDate(addHours(d, 24));
                  }
                  setPickupCalOpen(false);
                }}
                disabled={(date) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return date < today || isDateUnavailable(date);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <div className="relative">
            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
            <select
              value={pickupTime}
              onChange={(e) => setPickupTime(e.target.value)}
              className="w-full h-12 pl-9 pr-8 bg-neutral-50 border border-neutral-200 rounded-xl text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#111111]/10 hover:bg-neutral-100"
              data-testid="trip-pickup-time"
            >
              {TIME_SLOTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.display}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Trip End */}
      <div>
        <Label className="text-xs uppercase tracking-wide text-neutral-500">{t("tripScheduler.tripEnd")}</Label>
        <div className="grid grid-cols-2 gap-2 mt-1.5">
          <Popover open={returnCalOpen} onOpenChange={setReturnCalOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal h-12 bg-neutral-50 border-neutral-200 hover:bg-neutral-100 rounded-xl"
                data-testid="trip-return-date-btn"
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-neutral-500" />
                <span className="truncate">
                  {returnDate ? format(returnDate, "MM/dd/yyyy") : t("tripScheduler.date")}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={returnDate}
                onSelect={(d) => {
                  if (!d) return;
                  setReturnDate(d);
                  setReturnCalOpen(false);
                }}
                disabled={(date) => {
                  if (!pickupDate) return false;
                  const minD = new Date(pickupDate);
                  minD.setHours(0, 0, 0, 0);
                  return (
                    date < minD ||
                    isDateUnavailable(date) ||
                    (isSameDay(date, pickupDate) && false) // same-day allowed
                  );
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <div className="relative">
            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
            <select
              value={returnTime}
              onChange={(e) => setReturnTime(e.target.value)}
              className="w-full h-12 pl-9 pr-8 bg-neutral-50 border border-neutral-200 rounded-xl text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#111111]/10 hover:bg-neutral-100"
              data-testid="trip-return-time"
            >
              {TIME_SLOTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.display}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Validation / Conflict messages */}
      {!validation.valid && validation.error && (
        <p
          className="text-sm text-red-600 flex items-center gap-1.5"
          data-testid="trip-error"
        >
          <AlertCircle className="w-4 h-4" />
          {validation.error}
        </p>
      )}

      {conflict && (
        <div
          className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2"
          data-testid="trip-conflict"
        >
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <p className="font-semibold text-red-800">{t("tripScheduler.notAvailableTitle")}</p>
            <p className="text-red-700">
              {t("tripScheduler.notAvailableBody", {
                start: format(conflict.start, "PPp", { locale: dateLocale }),
                end: format(conflict.end, "PPp", { locale: dateLocale }),
              })}
            </p>
            <button
              type="button"
              onClick={suggestNext}
              className="mt-1.5 text-red-800 underline text-xs font-medium hover:text-red-900"
              data-testid="trip-suggest-next"
            >
              {t("tripScheduler.useNextSlot")}
            </button>
          </div>
        </div>
      )}

      {loadingAvail && (
        <p className="text-xs text-neutral-400 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          {t("tripScheduler.checkingAvailability")}
        </p>
      )}

      {/* Location */}
      <div>
        <Label className="text-xs uppercase tracking-wide text-neutral-500">
          {t("tripScheduler.pickupReturnLocation")}
        </Label>
        <div className="relative mt-1.5">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
          <Input
            value={locationLabel ? t("tripScheduler.onSiteAt", { location: locationLabel }) : t("tripScheduler.onSiteHost")}
            readOnly
            className="h-12 pl-9 bg-neutral-50 border-neutral-200 rounded-xl"
            data-testid="trip-location"
          />
        </div>
      </div>

      {/* Price summary */}
      {priceSummary && (
        <div
          className="border-t border-neutral-200 pt-4"
          data-testid="trip-price-summary"
        >
          <div className="flex justify-between text-sm mb-1">
            <span className="text-neutral-600">
              ${dailyRate?.toLocaleString()} MXN × {priceSummary.days}{" "}
              {t("tripScheduler.day", { count: priceSummary.days })}
            </span>
            {priceSummary.discountPct > 0 ? (
              <span className="flex items-center gap-2">
                <span className="text-neutral-400 line-through">
                  ${priceSummary.subtotal?.toLocaleString()}
                </span>
                <span className="font-semibold">
                  ${priceSummary.discounted?.toLocaleString()}
                </span>
              </span>
            ) : (
              <span className="font-semibold">
                ${priceSummary.subtotal?.toLocaleString()}{" "}
                <UsdHint amountMxn={priceSummary.subtotal} />
              </span>
            )}
          </div>
          {priceSummary.discountPct > 0 && (
            <p className="text-xs text-green-700">
              {t("tripScheduler.longStayDiscount", { pct: priceSummary.discountPct })}
            </p>
          )}
          <p className="text-xs text-neutral-500 mt-1">{t("tripScheduler.totalBeforeTaxes")}</p>
        </div>
      )}

      {/* CTA */}
      <Button
        type="button"
        onClick={onContinue}
        disabled={ctaDisabled}
        className={`w-full h-12 rounded-xl font-semibold transition ${
          ctaDisabled
            ? "bg-neutral-200 text-neutral-400 cursor-not-allowed"
            : "bg-[#111111] hover:bg-neutral-800 text-white"
        }`}
        data-testid="trip-continue-btn"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        {t("tripScheduler.continue")}
      </Button>
    </div>
  );
}
